import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, CheckCircle, Users, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const PricingInfo = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950">
      {/* Simplified background layers */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-purple-900/20 via-transparent to-transparent" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent" />
      
      {/* Static grid pattern (no animation) */}
      <div 
        className="fixed inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}
      />
      
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <Badge className="mb-4 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 text-white border-blue-500/30">
            Custom Pricing
          </Badge>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Tailored Solutions for{' '}
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Your Business
            </span>
          </h1>
          
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            We create custom pricing packages based on your specific needs and goals.
            Book a demo to discuss your requirements and get a personalized quote.
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-3xl border border-gray-700/50 p-8 md:p-12 shadow-2xl mb-12"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Why We Offer Custom Pricing
              </h2>
              
              <ul className="space-y-4">
                {[
                  "Every business has unique needs and goals",
                  "Volume-based pricing ensures you only pay for what you use",
                  "Flexible plans that grow with your business",
                  "Access to premium features based on your requirements"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-gray-900/90 to-gray-950/90 rounded-2xl p-6 md:p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-6 h-6 text-purple-400" />
                <h3 className="text-xl font-semibold text-white">Book a Demo</h3>
              </div>
              
              <p className="text-gray-400 mb-6">
                Schedule a personalized demo to see Munger in action and discuss your specific pricing needs.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  "See the platform in action",
                  "Discuss your specific requirements",
                  "Get a custom pricing quote",
                  "No obligation to purchase"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs">
                      {index + 1}
                    </div>
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/book-demo"
                className="inline-flex items-center justify-center w-full rounded-full bg-gradient-to-r from-blue-600 to-purple-600 py-3 px-6 text-white font-medium hover:from-blue-700 hover:to-purple-700 transition-colors"
              >
                Book Your Demo Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-gradient-to-br from-gray-800/30 to-gray-900/30 rounded-3xl border border-gray-700/50 p-8 md:p-12 shadow-xl"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                question: "Do you offer any free trials?",
                answer: "Yes, we discuss trial options during your demo based on your specific needs and use case."
              },
              {
                question: "How is pricing typically structured?",
                answer: "Our pricing is typically based on email volume and feature requirements, with flexible monthly plans."
              },
              {
                question: "Are there any long-term contracts?",
                answer: "We offer both month-to-month and annual options with discounts for longer commitments."
              },
              {
                question: "Can I upgrade or downgrade my plan?",
                answer: "Yes, you can adjust your plan as your needs change. We'll discuss flexibility options during your demo."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-900/60 to-gray-950/60 rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-3">{faq.question}</h3>
                <p className="text-gray-400">{faq.answer}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-6">
              Ready to learn more about our pricing options?
            </p>
            
            <Link
              to="/book-demo"
              className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-purple-600 py-3 px-8 text-white font-medium hover:from-blue-700 hover:to-purple-700 transition-colors"
            >
              Schedule Your Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </motion.div>
        
        <div className="mt-16 text-center">
          <Link
            to="/no-pricing"
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PricingInfo; 